import classNames from "classnames"
import { SelectorItemData } from "components/utils/Selector"
import { ReactNode } from "react"

interface SelectorItemProps<DataType, ValueType extends string | number> {
  data: SelectorItemData<DataType, ValueType>
  index: number
  selected: boolean
  prevSelected: boolean
  nextSelected: boolean
  inRange: boolean
  onChange: (val: ValueType) => void

  items: SelectorItemData<DataType, ValueType>[]
  values: ValueType[]
}

const SelectorItem = <DataType, ValueType extends string | number>({
  data,
  selected,
  prevSelected,
  nextSelected,
  inRange,
  onChange,
}: SelectorItemProps<DataType, ValueType>) => {
  const label =
    typeof data.label === "object"
      ? (data.label as ReactNode)
      : typeof data.label === "string"
        ? data.label
        : "" + data.value

  return (
    <>
      <style jsx>{`
        .selectorItem {
          flex: 1 0 max-content;
          //min-width: 40px;
          position: relative;
          width: 100%;
          min-height: 46px;
          display: flex;
          flex-direction: column;
          align-items: center;
          justify-content: center;
          border: none;
          background: none;
          font-size: 18px;
          border-radius: 10px;
          padding: 0;
          padding-left: 7px;
          padding-right: 7px;
          text-align: center;
          color: rgba(60, 60, 60, 1);
          line-height: 1;
          font-weight: bold;
        }
        .selectorItem::after {
          content: "";
          position: absolute;
          inset: 0;
          border-radius: 10px;
          box-shadow: var(--boxShadow);
          transition: opacity 0.2s ease-out;
          opacity: 0;
        }
        .selectorItem:active::after,
        .selectorItem:focus::after,
        .selectorItem:hover::after {
          opacity: 1;
        }
        .selectorItem:disabled {
          color: rgba(60, 60, 60, 0.4);
          pointer-events: none;
        }
        .discouraged {
          color: rgba(60, 60, 60, 0.6);
        }
        .discouraged:disabled {
          color: rgba(60, 60, 60, 0.2);
        }
        .selected {
          color: var(--gradientText);
        }
        .marked,
        .marked:focus {
          box-shadow:
            0 0 0 2px white,
            0 0 0 4px rgba(0, 0, 0, 0.3);
        }

        .selectorItem::before {
          content: "";
          position: absolute;
          z-index: -1;
          inset: 0;
          background: linear-gradient(0deg, var(--primary) 0%, var(--secondary) 100%);
          transition: all ease 0.25s;

          opacity: 0;
          transform: scale(0.5);
          border-radius: 10px;
          filter: grayscale(1);
        }
        .selected::before {
          transform: scale(1);
          opacity: 1;
          filter: grayscale(0);
        }
        .nextSelected::before {
          border-top-right-radius: 0;
          border-bottom-right-radius: 0;
        }
        .prevSelected::before {
          border-top-left-radius: 0;
          border-bottom-left-radius: 0;
        }
        .inRange::before {
          transform: scale(1);
          opacity: 0.3;
          border-radius: 0;
        }
      `}</style>
      <button
        type="button"
        onClick={() => onChange(data.value)}
        className={classNames({
          selectorItem: true,
          prevSelected: prevSelected,
          nextSelected: nextSelected,
          selected: selected,
          inRange: inRange,
          marked: data.marked,
          discouraged: data.discouraged,
        })}
        disabled={data.disabled}
      >
        {label}
      </button>
    </>
  )
}

export default SelectorItem
